import _, { isEmpty } from "lodash";
import React from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";

import { CUSTOM_VARIABLES } from "../genericIntegrationTicketConstants";
import { TemplateVariableDescriptionType } from "./UnifiedTicketTemplatePropTypes";
import {
  multiTemplateFieldDescription,
  singleTemplateFieldDescription,
  templateDetailsFieldDescription,
  templateFieldsDescription,
  templateTitleFieldDescription,
  ticketTopBottomVariableFieldKeys,
  variableRegex,
} from "./constants";

export const VARIABLE_PREFIX = "{var.";

export const mentionsFieldProps = () => ({
  prefix: "$",
  placement: "bottom",
});

export const ticketTopBottomMentionFieldProps = () => ({
  prefix: "$",
  placement: "bottom",
  options: ticketTopBottomVariableFieldKeys.map((fields) => `{${fields}}`),
});

const removeKeyFromTag = (name: string) =>
  name.indexOf("finding.tag.") !== -1 || name.indexOf("project.tag.") !== -1
    ? name.split("<key>").shift()
    : name.split("<labelName>").shift();

export const findingFieldValidator = (
  e: any,
  value: string,
  supportedFields: string[],
) => {
  return commonTemplateFieldsValidator(e, value, supportedFields);
};

export const labelFieldValidator = (e: any, value: string, fields: any) => {
  return commonTemplateFieldsValidator(e, value, [
    "runbookname",
    ...Object.keys(fields),
  ]);
};

export const commonTemplateFieldsValidator = (
  e: any,
  value: string,
  supportedFields: string | string[],
) => {
  if (typeof value === "string" && value?.trim().length !== 0) {
    const totalFields: Array<string> = value.match(variableRegex) || [];

    // filter out runbook ${var.} fields and ${runbook} field while validating field names
    const filterdFields = _.filter(
      totalFields,
      (entry) =>
        entry.indexOf(VARIABLE_PREFIX) === -1 && entry !== "${runbookname}",
    );

    const fields = _.map(filterdFields, (match) => {
      let text = match;
      // remove default value from field (if any)
      if (text.indexOf("|")) {
        text = text?.split("|")?.shift()?.trim() + "}";
      }

      return text?.split("{")?.pop()?.split("}").shift();
    });

    const missing$ErrorForFindingVariable =
      check$PrefixTicketTemplateFields(value);
    if (missing$ErrorForFindingVariable) {
      return Promise.reject(missing$ErrorForFindingVariable);
    }

    const hasSpacesInsideBraces = checkSpaceInsideBraces(value);
    if (!isEmpty(hasSpacesInsideBraces)) {
      return Promise.reject(`${hasSpacesInsideBraces![0]} has spaces`);
    }

    const hasMissingBracesError =
      findTicketTemplateVariableMissingBraces(value);
    if (hasMissingBracesError) {
      return Promise.reject(hasMissingBracesError);
    }

    const sanitizedFieldNames = _.map(supportedFields, removeKeyFromTag);
    const tagNameMissing = fields.filter(
      (field) =>
        field &&
        !isEmpty(
          CUSTOM_VARIABLES.find((variable) => variable.indexOf(field) !== -1),
        ) &&
        (field.split("tag.").pop() === "" ||
          field.split("customField.").pop() === "" ||
          field.split("tag.").pop() === "<key>" ||
          field.split("customField.").pop() === "<labelName>" ||
          field.split("project.tag.").pop() === "" ||
          field.split("project.tag.").pop() === "<key>"),
    );
    if (tagNameMissing.length > 0) {
      return Promise.reject("Tag/Custom Field name is missing");
    }

    const errorfields = fields.filter((field) => {
      const _field =
        field && field.indexOf("finding.tag.") !== -1
          ? "finding.tag."
          : field?.indexOf("finding.customField.") !== -1
            ? "finding.customField."
            : field?.indexOf("project.tag.") !== -1
              ? "project.tag."
              : field;
      return sanitizedFieldNames.indexOf(_field) === -1;
    });

    if (errorfields.length > 0) {
      const errorMessage = `${errorfields.join(", ")} ${errorfields.length === 1 ? "is" : "are"} invalid finding field(s) and/or has spaces.`;
      return Promise.reject(errorMessage);
    }
  }
  return Promise.resolve();
};

export const unifiedTicketTemplateVariableFieldsValidator = (
  inputValue: string,
  allowedVariables: string[],
) => {
  if (inputValue) {
    const missing$ErrorForFindingVariable =
      check$PrefixTicketTemplateFields(inputValue);
    if (missing$ErrorForFindingVariable) {
      return Promise.reject(missing$ErrorForFindingVariable);
    }

    const hasSpacesInsideBraces = checkSpaceInsideBraces(inputValue);
    if (!isEmpty(hasSpacesInsideBraces)) {
      return Promise.reject(`${hasSpacesInsideBraces![0]} has spaces`);
    }

    const hasMissingBracesError =
      findTicketTemplateVariableMissingBraces(inputValue);
    if (hasMissingBracesError) {
      return Promise.reject(hasMissingBracesError);
    }

    const variableRegex = /\${\S*?}/g;
    const matches = inputValue.match(variableRegex) || [];
    for (const match of matches) {
      const variableName = match.slice(2, -1);
      const isValidVariable = allowedVariables.includes(variableName);
      const isTagOrCustomField =
        variableName.startsWith("finding.tag.") ||
        variableName.startsWith("finding.customField.");
      const isValidTagOrCustomField =
        /^finding\.(tag|customField)\.(?!(<key>|<labelName>)$)\S+$/.test(
          variableName,
        );
      const attributeNameRegexCheck =
        /^finding\.derivedAttributes\.<attributeName>$/;
      const isAttributeName = variableName.startsWith(
        "finding.derivedAttributes.",
      );
      if (
        variableName === "" ||
        (!isTagOrCustomField && !isValidVariable && !isAttributeName)
      ) {
        return Promise.reject(
          `${match} is invalid finding field(s) and/or has spaces.`,
        );
      } else if (isTagOrCustomField && !isValidTagOrCustomField) {
        return Promise.reject("Tag/Custom Field name is missing");
      } else if (
        isAttributeName &&
        attributeNameRegexCheck.test(variableName)
      ) {
        return Promise.reject("Attribute name is missing");
      }
    }
  }
  return Promise.resolve();
};

export const unifiedTicketTemplateVariableTopBottomFieldsValidator = (
  inputValue: string,
  allowedVariables: string[],
) => {
  if (inputValue) {
    const missing$ErrorForFindingVariable =
      check$PrefixTicketTemplateFields(inputValue);
    if (missing$ErrorForFindingVariable) {
      return Promise.reject(missing$ErrorForFindingVariable);
    }

    const hasSpacesInsideBraces = checkSpaceInsideBraces(inputValue);
    if (!isEmpty(hasSpacesInsideBraces)) {
      return Promise.reject(`${hasSpacesInsideBraces![0]} has spaces`);
    }

    const hasMissingBracesError =
      findTicketTemplateVariableMissingBraces(inputValue);
    if (hasMissingBracesError) {
      return Promise.reject(hasMissingBracesError);
    }

    const variableRegex = /\${\S*?}/g;
    const matches = inputValue.match(variableRegex) || [];
    for (const match of matches) {
      const variableName = match.slice(2, -1);
      const isValidVariable = allowedVariables.includes(variableName);
      const isCustomField = variableName.startsWith("finding.customField.");
      const isValidCustomField =
        /^finding\.customField\.(?!<labelName>$)\S+$/.test(variableName);
      const attributeNameRegexCheck =
        /^finding\.derivedAttributes\.<attributeName>$/;
      const isAttributeName = variableName.startsWith(
        "finding.derivedAttributes.",
      );
      if (
        variableName === "" ||
        (!isCustomField && !isValidVariable && !isAttributeName)
      ) {
        return Promise.reject(
          `${match} is invalid finding field(s) and/or has spaces.`,
        );
      } else if (isCustomField && !isValidCustomField) {
        return Promise.reject("Custom Field name is missing");
      } else if (
        isAttributeName &&
        attributeNameRegexCheck.test(variableName)
      ) {
        return Promise.reject("Attribute name is missing");
      }
    }
  }
  return Promise.resolve();
};

export const templateNameValidator = (
  value: string,
  existingTemplateName: string,
  apiCancelRef: React.MutableRefObject<any>,
) => {
  if (
    !isEmpty(value) &&
    existingTemplateName?.toLowerCase() !== value.toLowerCase()
  ) {
    if (apiCancelRef.current) {
      apiCancelRef.current();
    }

    const { promise, cancel } =
      ToolsIntegrationsAPI.validateUnifiedTicketTemplateName(
        { name: value },
        false,
      );
    apiCancelRef.current = cancel;
    return promise.catch(() => Promise.reject(`Template Name already exists.`));
  }
  return Promise.resolve();
};

export const getTemplateFieldVariableOptions = (
  field: string,
  type?: string,
): string[] => {
  let fieldDescription: TemplateVariableDescriptionType = {};
  switch (field) {
    case "title":
      fieldDescription = { ...templateTitleFieldDescription };
      if (type === "MULTI") {
        fieldDescription = {
          ...templateTitleFieldDescription,
          ...multiTemplateFieldDescription,
        };
      }
      break;
    case "details":
      fieldDescription = {
        ...templateDetailsFieldDescription,
        ...singleTemplateFieldDescription,
      };
      if (type === "MULTI") {
        fieldDescription = templateDetailsFieldDescription;
      }
      break;
    default:
      fieldDescription = templateFieldsDescription;
      break;
  }

  const templateFieldOptions = Object.keys(fieldDescription);

  return templateFieldOptions.map((fields) => `{${fields}}`);
};

const check$PrefixTicketTemplateFields = (value: string) => {
  const regexFor$Miss = /(?<!\$)\{[a-zA-Z0-9._\s]+\}/gm;
  const fieldsWithout$ = value.match(regexFor$Miss);
  // only check for fields which has 'runbookname', 'finding.' or 'var.' while checking for $ sign, ignore others
  const findingAndVarFieldsWithoutDollar =
    fieldsWithout$?.filter(
      (field) =>
        field.indexOf("var.") !== -1 ||
        field.indexOf("finding.") !== -1 ||
        field.indexOf("runbookname") !== -1,
    ) || [];

  if (findingAndVarFieldsWithoutDollar.length > 0) {
    return `$ is missing from ${findingAndVarFieldsWithoutDollar.join(", ")}`;
  }
};

const checkSpaceInsideBraces = (value: string) => {
  const checkSpaceRegex = /\${[^}]*?\s[^}]*?}/g;
  return checkSpaceRegex.exec(value);
};

const findTicketTemplateVariableMissingBraces = (
  value: string,
): string | null => {
  const errorRules = [
    {
      // Case 1: Detects patterns like `${text` (missing the closing '}').
      regex: /\$\{[^}\n]*(?=\s|$)/g,
      message: "is missing a closing brace '}'",
    },
    {
      // Case 2: Detects patterns like `$text}` (missing the opening '{').
      regex: /\$[^}{]*?\}/g,
      message: "is missing an opening brace '{'",
    },
    {
      // Case 3: Detects patterns like `$text` (missing both braces).
      regex: /\$[^}{\s]+/g,
      message: "is missing both braces",
    },
  ];
  for (const rule of errorRules) {
    const match = value.match(rule.regex);
    if (match) {
      return `'${match[0]}' ${rule.message}.`;
    }
  }
  return null;
};
